/**
 * Misc Income (type="INCOME") / Misc Expense (type="EXPENSE") list.
 * Filters: date range, category, mode, status, search. Actions: print, edit, cancel.
 */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Card, Table, Button, Form, Badge, Modal, Spinner, InputGroup } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import schoolApi from "../../api/schoolApi";
import { SessionContext } from "../SessionProvider";
import LedgerEntryModal from "./LedgerEntryModal";
import LedgerReceipt from "./LedgerReceipt";
import { TYPE_INFO, PAYMENT_METHODS, inr, fmtDate, partyName, monthStart, today, useCan, num } from "./ledgerUtils";

const LedgerEntries = ({ type = "INCOME", tabName }) => {
  const info = TYPE_INFO[type];
  const can = useCan(info.permission);
  const { selectedSessionId } = useContext(SessionContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ from: monthStart(), to: today(), category_id: "", payment_method: "", status: "active", q: "" });
  const [modal, setModal] = useState({ show: false, entry: null });
  const [cancelFor, setCancelFor] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [printEntry, setPrintEntry] = useState(null);
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Receipt",
    onAfterPrint: () => setPrintEntry(null),
  });
  useEffect(() => {
    if (printEntry) setTimeout(() => handlePrint(), 50);
  }, [printEntry]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCategories = useCallback(async () => {
    try {
      const r = await schoolApi.ledgerGet("/categories", { type });
      setCategories(r?.result || []);
    } catch {
      setCategories([]);
    }
  }, [type]);

  const load = useCallback(async () => {
    if (!selectedSessionId) return;
    setLoading(true);
    try {
      const params = { type, session_id: selectedSessionId };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await schoolApi.ledgerGet("/entries", params);
      setRows(r?.result || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not load entries");
    } finally {
      setLoading(false);
    }
  }, [type, selectedSessionId, filters]);

  useEffect(() => { loadCategories(); }, [loadCategories]);
  useEffect(() => {
    const t = setTimeout(load, filters.q ? 350 : 0);
    return () => clearTimeout(t);
  }, [load, filters.q]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const totals = useMemo(() => {
    const active = rows.filter((r) => r.status === "active");
    const byMode = {};
    active.forEach((r) => (byMode[r.payment_method || "Cash"] = (byMode[r.payment_method || "Cash"] || 0) + num(r.amount)));
    return { count: active.length, amount: active.reduce((a, r) => a + num(r.amount), 0), byMode };
  }, [rows]);

  const doCancel = async () => {
    if (!cancelReason.trim()) return toast.warn("Write the reason");
    try {
      const r = await schoolApi.ledgerPost(`/entries/${cancelFor.id}/cancel`, { reason: cancelReason.trim() });
      if (r?.success) {
        toast.success(`${cancelFor.receipt_number} cancelled`);
        setCancelFor(null);
        load();
      } else toast.error(r?.message || "Could not cancel");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not cancel");
    }
  };

  return (
    <>
      <Helmet><title>{tabName || info.title}</title></Helmet>
      <Row className="g-0 px-lg-4 px-2">
        <Col lg={12} className="d-flex flex-wrap justify-content-between align-items-center mt-3 mb-2 gap-2">
          <Link className="nav-link" to="/">
            Home <i className="fa-solid fa-chevron-right" /> Accounts <i className="fa-solid fa-chevron-right" /> {info.title}
          </Link>
          {can.create && (
            <Button style={{ background: info.color, borderColor: info.color }} onClick={() => setModal({ show: true, entry: null })}>
              <i className="fa-solid fa-plus me-1" /> {info.newLabel}
            </Button>
          )}
        </Col>

        {/* summary */}
        <Col lg={12}>
          <Row className="g-2 mb-3">
            <Col md={3}>
              <Card className="h-100 shadow-sm" style={{ borderLeft: `4px solid ${info.color}` }}>
                <Card.Body className="py-2">
                  <div className="small text-muted">Total {type === "INCOME" ? "received" : "paid"} ({fmtDate(filters.from)} – {fmtDate(filters.to)})</div>
                  <div className="fs-4 fw-bold" style={{ color: info.color }}>₹ {inr(totals.amount)}</div>
                  <div className="small text-muted">{totals.count} entries</div>
                </Card.Body>
              </Card>
            </Col>
            {Object.entries(totals.byMode).map(([m, a]) => (
              <Col md={2} key={m}>
                <Card className="h-100 shadow-sm">
                  <Card.Body className="py-2">
                    <div className="small text-muted">{m}</div>
                    <div className="fs-5 fw-semibold">₹ {inr(a)}</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Col>

        {/* filters */}
        <Col lg={12}>
          <Card className="shadow-sm mb-3">
            <Card.Body className="py-2">
              <Row className="g-2 align-items-end">
                <Col md={2}>
                  <Form.Label className="small mb-0">From</Form.Label>
                  <Form.Control size="sm" type="date" value={filters.from} onChange={(e) => setF("from", e.target.value)} />
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-0">To</Form.Label>
                  <Form.Control size="sm" type="date" value={filters.to} onChange={(e) => setF("to", e.target.value)} />
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-0">Category</Form.Label>
                  <Form.Select size="sm" value={filters.category_id} onChange={(e) => setF("category_id", e.target.value)}>
                    <option value="">All</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Form.Label className="small mb-0">Mode</Form.Label>
                  <Form.Select size="sm" value={filters.payment_method} onChange={(e) => setF("payment_method", e.target.value)}>
                    <option value="">All</option>
                    {PAYMENT_METHODS.map((m) => <option key={m}>{m}</option>)}
                  </Form.Select>
                </Col>
                <Col md={1}>
                  <Form.Label className="small mb-0">Status</Form.Label>
                  <Form.Select size="sm" value={filters.status} onChange={(e) => setF("status", e.target.value)}>
                    <option value="active">Active</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="">All</option>
                  </Form.Select>
                </Col>
                <Col md={3}>
                  <InputGroup size="sm">
                    <InputGroup.Text><i className="fa-solid fa-magnifying-glass" /></InputGroup.Text>
                    <Form.Control placeholder="Receipt no., name, remark" value={filters.q} onChange={(e) => setF("q", e.target.value)} />
                  </InputGroup>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* table */}
        <Col lg={12}>
          <Card className="shadow-sm">
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0 align-middle" size="sm">
                <thead style={{ background: "#637384", color: "#fff" }}>
                  <tr>
                    <th className="ps-3">Receipt No.</th>
                    <th>Date</th>
                    <th>Category</th>
                    <th>{info.partyLabel}</th>
                    <th>Mode</th>
                    <th className="text-end">Amount (₹)</th>
                    <th>Remark</th>
                    <th className="text-end pe-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={8} className="text-center py-4"><Spinner animation="border" size="sm" /></td></tr>
                  )}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={8} className="text-center text-muted py-4">No entries for this filter</td></tr>
                  )}
                  {!loading && rows.map((r) => (
                    <tr key={r.id} style={r.status === "cancelled" ? { opacity: 0.55 } : undefined}>
                      <td className="ps-3 fw-semibold">
                        {r.receipt_number}{" "}
                        {r.status === "cancelled" && <Badge bg="danger" title={r.cancel_reason}>Cancelled</Badge>}
                      </td>
                      <td>{fmtDate(r.entry_date)}</td>
                      <td>{r.category_name}</td>
                      <td>
                        {partyName(r)}
                        {r.party_type === "STUDENT" && r.classname && (
                          <div className="small text-muted">
                            Class {r.classname}{r.sectionname ? ` - ${r.sectionname}` : ""}{r.admission_session ? ` · ${r.admission_session}` : ""}
                          </div>
                        )}
                      </td>
                      <td>{r.payment_method}{r.transaction_no ? <div className="small text-muted">{r.transaction_no}</div> : null}</td>
                      <td className="text-end fw-semibold">{inr(r.amount)}</td>
                      <td className="small" style={{ maxWidth: 220 }}>{r.remark}</td>
                      <td className="text-end pe-3 text-nowrap">
                        <Button size="sm" variant="outline-secondary" title="Print" onClick={() => setPrintEntry(r)}>
                          <i className="fa-solid fa-print" />
                        </Button>{" "}
                        {r.status === "active" && can.edit && (
                          <Button size="sm" variant="outline-primary" title="Edit" onClick={() => setModal({ show: true, entry: r })}>
                            <i className="fa-solid fa-pen" />
                          </Button>
                        )}{" "}
                        {r.status === "active" && (can.del || can.edit) && (
                          <Button size="sm" variant="outline-danger" title="Cancel" onClick={() => { setCancelReason(""); setCancelFor(r); }}>
                            <i className="fa-solid fa-ban" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <LedgerEntryModal
        show={modal.show}
        entry={modal.entry}
        type={type}
        sessionId={selectedSessionId}
        categories={categories}
        onHide={() => setModal({ show: false, entry: null })}
        onSaved={(saved, isNew) => {
          load();
          if (isNew && saved) setPrintEntry(saved);
        }}
      />

      <Modal show={!!cancelFor} onHide={() => setCancelFor(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 17 }}>Cancel {cancelFor?.receipt_number}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            ₹ {inr(cancelFor?.amount)} · {cancelFor?.category_name} · {partyName(cancelFor)}. The entry stays in the list as
            cancelled and is not counted in totals.
          </p>
          <Form.Control as="textarea" rows={2} maxLength={255} placeholder="Reason" value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setCancelFor(null)}>Close</Button>
          <Button variant="danger" onClick={doCancel}>Cancel Entry</Button>
        </Modal.Footer>
      </Modal>

      <div style={{ display: "none" }}>
        <LedgerReceipt ref={printRef} entry={printEntry} />
      </div>
    </>
  );
};

export default LedgerEntries;
